import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, ChevronUp, List, BookOpen } from 'lucide-react';

const TableOfContents = ({ onNavigate }) => {
  const chapters = [
    {
      id: 'chapter-1',
      title: 'Chapter 1: Separate Worlds',
      subtitle: 'Meet Nancy and Ned in their everyday lives'
    },
    {
      id: 'chapter-2',
      title: 'Chapter 2: An Unlikely Pair',
      subtitle: 'A summer project brings unexpected changes'
    }
  ];

  return (
    <div className="p-4">
      <h3 className="font-semibold text-lg mb-4 text-gray-900">Table of Contents</h3>
      <nav className="space-y-4">
        {chapters.map((chapter) => (
          <button
            key={chapter.id}
            onClick={() => onNavigate(chapter.id)}
            className="block w-full text-left group"
          >
            <div className="px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors">
              <div className="text-sm font-medium text-gray-900 group-hover:text-amber-600">
                {chapter.title}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {chapter.subtitle}
              </div>
            </div>
          </button>
        ))}
      </nav>
    </div>
  );
};

export const BookPanel = ({ isOpen, onClose, title, children }) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showToc, setShowToc] = useState(false);

  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      const scrolled = target.scrollTop > 500;
      setShowScrollTop(scrolled);

      // Calculate reading progress
      const progress = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
      setReadingProgress(Math.min(100, Math.max(0, progress)));
    };

    const content = document.getElementById('book-content');
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen]);

  const scrollToTop = () => {
    document.getElementById('book-content')?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleChapterNavigation = (chapterId) => {
    const element = document.getElementById(chapterId);
    if (element) {
      const contentElement = document.getElementById('book-content');
      if (contentElement) {
        const headerHeight = 80;
        const elementPosition = element.offsetTop - headerHeight;

        contentElement.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }

      if (window.innerWidth < 768) {
        setShowToc(false);
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-4xl">
                  <div className="flex h-full flex-col overflow-hidden bg-[#faf9f7]">
                    {/* Header */}
                    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm px-4 py-6 sm:px-6 border-b border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Dialog.Title className="text-2xl font-bold text-gray-900 flex items-center">
                            <BookOpen className="mr-2 h-6 w-6 text-amber-500" />
                            {title || "Ned and Nancy"}
                          </Dialog.Title>
                        </div>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => setShowToc(!showToc)}
                            className="text-gray-500 hover:text-amber-600 transition-colors"
                            aria-label="Toggle table of contents"
                          >
                            <List className="h-6 w-6" />
                          </button>
                          <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            aria-label="Close reader"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                      {/* Reading Progress Bar */}
                      <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-200">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                          style={{ width: `${readingProgress}%` }}
                        />
                      </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="relative flex-1 flex overflow-hidden">
                      {/* Table of Contents Sidebar */}
                      <div
                        className={`absolute inset-y-0 left-0 w-64 bg-gray-50 border-r border-gray-200 z-10 transform transition-transform duration-300 ${
                          showToc ? 'translate-x-0' : '-translate-x-full'
                        }`}
                      >
                        <TableOfContents onNavigate={handleChapterNavigation} />
                      </div>

                      {/* Content Area */}
                      <div
                        id="book-content"
                        className="flex-1 overflow-y-auto px-4 sm:px-8 py-6"
                        style={{
                          backgroundColor: '#faf9f7',
                          color: '#1a1a1a'
                        }}
                      >
                        {children}
                      </div>
                    </div>

                    {/* Scroll to Top Button */}
                    {showScrollTop && (
                      <div className="fixed bottom-4 right-4">
                        <button
                          onClick={scrollToTop}
                          className="bg-amber-500 text-white p-3 rounded-full shadow-lg hover:bg-amber-600 transition-colors"
                          aria-label="Scroll to top"
                        >
                          <ChevronUp className="h-6 w-6" />
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BookPanel;
